import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState
} from 'react';
import PropTypes from 'prop-types';
import { FixedSizeList } from 'react-window';
import moment from 'moment';
import Select from 'react-select';
import AppContext from 'context/Context';
import './LiveDiscrepancyVirtualizedTable.scss';

const ROW_HEIGHT = 64;
const LIST_HEIGHT = 600;
const HEADER_HEIGHT = 40;
const MIN_COL_WIDTH = 90;
const MAX_COL_WIDTH = 420;
const CELL_PADDING_X = 12; // matches cell style: padding: '8px 12px'
const CELL_FONT =
  '400 12.8px -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Arial, sans-serif';
const HEADER_FONT =
  '600 12.8px -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Arial, sans-serif';

const statusOptions = [
  { value: 0, label: 'Outstanding' },
  { value: 1, label: 'Resolved' }
];

const COLUMN_DEFS = [
  { key: 'dateRaised', header: 'Date raised', autoFit: true },
  { key: 'supplierName', header: 'Supplier', autoFit: true },
  { key: 'wpoRef', header: 'WPO ref', autoFit: true },
  { key: 'grnRef', header: 'GRN ref', autoFit: true },
  { key: 'partCode', header: 'Part code', autoFit: true },
  { key: 'supplierPartCode', header: 'Supplier part code', autoFit: true },
  { key: 'partDescription', header: 'Part description', autoFit: true },
  { key: 'deliveryNoteQty', header: 'Del note QTY', autoFit: true },
  { key: 'receivedQty', header: 'Received QTY', autoFit: true },
  { key: 'images', header: 'Images', autoFit: false, width: 90 },
  { key: 'notes', header: 'Notes', autoFit: false, width: 120 },
  { key: 'status', header: 'Status', autoFit: false, width: 190 }
];

let measureCanvasCtx = null;
const getTextWidth = (text, font) => {
  if (!measureCanvasCtx) {
    const canvas = document.createElement('canvas');
    measureCanvasCtx = canvas.getContext('2d');
  }
  measureCanvasCtx.font = font;
  return measureCanvasCtx.measureText(text ?? '').width;
};

const formatValueForDisplay = (row, key) => {
  if (key === 'dateRaised') {
    return row.dateRaised
      ? moment.utc(row.dateRaised).local().format('DD/MM/YYYY HH:mm')
      : '';
  }
  const value = row[key];
  return value === null || value === undefined ? '' : String(value);
};

const useAutoFitColumns = data =>
  useMemo(() => {
    return COLUMN_DEFS.map(col => {
      if (!col.autoFit) return col;

      let maxWidth = getTextWidth(col.header, HEADER_FONT);
      data.forEach(row => {
        const text = formatValueForDisplay(row, col.key);
        const width = getTextWidth(text, CELL_FONT);
        if (width > maxWidth) maxWidth = width;
      });

      const width = Math.min(
        MAX_COL_WIDTH,
        Math.max(MIN_COL_WIDTH, Math.ceil(maxWidth) + CELL_PADDING_X * 2 + 8)
      );
      return { ...col, width };
    });
  }, [data]);

const LiveDiscrepancyRow = ({ index, style, data: itemData }) => {
  const {
    rows,
    columns,
    totalWidth,
    stripeColors,
    borderColor,
    textColor,
    onViewImages,
    onViewNotes,
    onStatusChange
  } = itemData;
  const row = rows[index];
  const rowBg = index % 2 === 0 ? stripeColors.even : stripeColors.odd;

  const cellStyle = {
    display: 'flex',
    alignItems: 'center',
    padding: `8px ${CELL_PADDING_X}px`,
    borderRight: `1px solid ${borderColor}`,
    borderBottom: `1px solid ${borderColor}`,
    boxSizing: 'border-box',
    flexGrow: 1,
    flexShrink: 0,
    fontSize: '0.8rem',
    color: textColor,
    overflow: 'hidden',
    whiteSpace: 'nowrap',
    textOverflow: 'ellipsis'
  };

  const renderCellContent = column => {
    switch (column.key) {
      case 'dateRaised':
        return moment.utc(row.dateRaised).local().format('DD/MM/YYYY HH:mm');
      case 'images':
        return (
          <span
            className="text-primary cursor-pointer"
            onClick={() =>
              onViewImages({ grn: row.grnRef, wpoRef: row.wpoRef })
            }
          >
            View
          </span>
        );
      case 'notes':
        return (
          <span
            className="text-primary cursor-pointer"
            onClick={() => onViewNotes({ id: row.id })}
          >
            View ({row.noteCount})
          </span>
        );
      case 'status':
        return (
          <Select
            isMulti={false}
            name="status"
            options={statusOptions}
            value={
              statusOptions.find(x => x.value === row.status) ||
              statusOptions[0]
            }
            onChange={option => onStatusChange(row.id, option?.value)}
            classNamePrefix="react-select"
            placeholder="Select a status..."
            menuPortalTarget={document.body}
            styles={{
              menuPortal: base => ({ ...base, zIndex: 9999 }),
              container: base => ({ ...base, width: '100%' })
            }}
          />
        );
      default:
        return row[column.key];
    }
  };

  return (
    <div
      className="live-discrepancy-row"
      style={{
        ...style,
        minWidth: totalWidth,
        width: '100%',
        display: 'flex',
        background: rowBg,
        transition: 'background-color 0.1s ease-in-out'
      }}
    >
      {columns.map(column => (
        <div
          key={column.key}
          style={{
            ...cellStyle,
            flexBasis: column.width
          }}
          title={
            column.key === 'status' ||
            column.key === 'images' ||
            column.key === 'notes'
              ? undefined
              : row[column.key]
          }
        >
          {renderCellContent(column)}
        </div>
      ))}
    </div>
  );
};

LiveDiscrepancyRow.propTypes = {
  index: PropTypes.number.isRequired,
  style: PropTypes.object.isRequired,
  data: PropTypes.shape({
    rows: PropTypes.array.isRequired,
    columns: PropTypes.array.isRequired,
    totalWidth: PropTypes.number.isRequired,
    stripeColors: PropTypes.shape({
      even: PropTypes.string.isRequired,
      odd: PropTypes.string.isRequired
    }).isRequired,
    borderColor: PropTypes.string.isRequired,
    textColor: PropTypes.string.isRequired,
    onViewImages: PropTypes.func.isRequired,
    onViewNotes: PropTypes.func.isRequired,
    onStatusChange: PropTypes.func.isRequired
  }).isRequired
};

const LiveDiscrepancyVirtualizedTable = ({
  data,
  onViewImages,
  onViewNotes,
  onStatusChange
}) => {
  const {
    config: { isDark }
  } = useContext(AppContext);

  const [sortState, setSortState] = useState({ key: null, desc: false });

  const [listOuterEl, setListOuterEl] = useState(null);
  const listOuterRef = useCallback(node => setListOuterEl(node), []);
  const headerScrollRef = useRef(null);

  useEffect(() => {
    if (!listOuterEl) return undefined;
    const onScroll = () => {
      if (headerScrollRef.current) {
        headerScrollRef.current.style.transform = `translateX(-${listOuterEl.scrollLeft}px)`;
      }
    };
    listOuterEl.addEventListener('scroll', onScroll);
    return () => listOuterEl.removeEventListener('scroll', onScroll);
  }, [listOuterEl]);

  const columns = useAutoFitColumns(data);
  const totalWidth = useMemo(
    () => columns.reduce((sum, col) => sum + col.width, 0),
    [columns]
  );

  const sortedData = useMemo(() => {
    if (!sortState.key) return data;
    const copy = [...data];
    copy.sort((a, b) => {
      const av = a?.[sortState.key];
      const bv = b?.[sortState.key];
      if (av == null && bv == null) return 0;
      if (av == null) return 1;
      if (bv == null) return -1;
      if (av < bv) return sortState.desc ? 1 : -1;
      if (av > bv) return sortState.desc ? -1 : 1;
      return 0;
    });
    return copy;
  }, [data, sortState]);

  const borderColor = isDark ? '#4a4f58' : '#dee2e6';
  const textColor = isDark ? '#ffffff' : '#000000';
  const headerBg = isDark ? 'rgb(34 45 59)' : '#d8e2ef';
  const headerTextColor = isDark ? '#ffffff' : '#354052';

  const stripeColors = useMemo(
    () =>
      isDark
        ? { even: '#1e1e2f', odd: '#262838' }
        : { even: '#ffffff', odd: '#f8f9fa' },
    [isDark]
  );

  const itemData = useMemo(
    () => ({
      rows: sortedData,
      columns,
      totalWidth,
      stripeColors,
      borderColor,
      textColor,
      onViewImages,
      onViewNotes,
      onStatusChange
    }),
    [
      sortedData,
      columns,
      totalWidth,
      stripeColors,
      borderColor,
      textColor,
      onViewImages,
      onViewNotes,
      onStatusChange
    ]
  );

  const handleHeaderClick = useCallback(column => {
    if (!column.autoFit) return;
    setSortState(prev =>
      prev.key === column.key
        ? { key: column.key, desc: !prev.desc }
        : { key: column.key, desc: false }
    );
  }, []);

  return (
    <div
      style={{
        border: `1px solid ${borderColor}`,
        borderRadius: '6px',
        overflow: 'hidden',
        boxShadow: isDark
          ? '0 1px 3px rgba(0, 0, 0, 0.4)'
          : '0 1px 3px rgba(0, 0, 0, 0.08)'
      }}
    >
      <div style={{ overflow: 'hidden' }}>
        <div
          ref={headerScrollRef}
          style={{ display: 'flex', minWidth: totalWidth, width: '100%' }}
        >
          {columns.map(column => (
            <div
              key={column.key}
              onClick={() => handleHeaderClick(column)}
              style={{
                display: 'flex',
                alignItems: 'center',
                flexGrow: 1,
                flexShrink: 0,
                flexBasis: column.width,
                height: HEADER_HEIGHT,
                padding: `8px ${CELL_PADDING_X}px`,
                boxSizing: 'border-box',
                borderRight: `1px solid ${borderColor}`,
                borderBottom: `2px solid ${borderColor}`,
                fontWeight: 600,
                fontSize: '0.8rem',
                color: headerTextColor,
                background: headerBg,
                cursor: column.autoFit ? 'pointer' : 'default',
                userSelect: 'none'
              }}
            >
              {column.header}
              {sortState.key === column.key && (sortState.desc ? ' ▼' : ' ▲')}
            </div>
          ))}
        </div>
      </div>

      {sortedData.length === 0 ? (
        <div
          className="text-center p-4"
          style={{ color: textColor, background: stripeColors.even }}
        >
          No Results
        </div>
      ) : (
        <FixedSizeList
          height={LIST_HEIGHT}
          width="100%"
          itemCount={sortedData.length}
          itemSize={ROW_HEIGHT}
          itemData={itemData}
          outerRef={listOuterRef}
        >
          {LiveDiscrepancyRow}
        </FixedSizeList>
      )}
    </div>
  );
};

LiveDiscrepancyVirtualizedTable.propTypes = {
  data: PropTypes.arrayOf(PropTypes.object),
  onViewImages: PropTypes.func.isRequired,
  onViewNotes: PropTypes.func.isRequired,
  onStatusChange: PropTypes.func.isRequired
};

LiveDiscrepancyVirtualizedTable.defaultProps = {
  data: []
};

export default LiveDiscrepancyVirtualizedTable;
